import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { fetchDocxAsArrayBuffer } from '../utils/docxLoader';
import { parseDocxToQuiz } from '../utils/parseDocxToQuiz';
import { storage } from '../firebase';
import { ref, getBlob } from 'firebase/storage';
import { toastManager } from '../utils/toastManager';
import { recordCompletion } from '../utils/quizSessionManager';
import { generateAttemptPdf } from '../utils/pdfReport';
import { resolveQuestionNumberFromQuizItem } from '../utils/quizReport';
import { Clock, Copy, Check, ClipboardList } from 'lucide-react';
import './PlayerQuiz.css';

/** When question count exceeds this, list is treated as scrollable and we use start alignment so 1,2,3... show at top. */
const QUESTIONS_OVERFLOW_THRESHOLD = 25;

const PlayerQuiz = ({ quizUrl, sessionId, sessionTitle, player, timeLimit = 1800, isAdmin = false, canReview = false }) => {
  const [quizData, setQuizData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [playerDisplayName, setPlayerDisplayName] = useState('');
  const [quizState, setQuizState] = useState('name_required'); // name_required, loading, playing, completed
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'finish' }
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [answerError, setAnswerError] = useState('');
  const [answersLocked, setAnswersLocked] = useState(false);
  const [completionId, setCompletionId] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const timerEndRef = useRef(null);
  const indicatorsScrollRef = useRef(null);
  const [indicatorsOverflow, setIndicatorsOverflow] = useState(false);
  const submittedRef = useRef(false);
  const selectedAnswersRef = useRef({});
  const quizDataRef = useRef(null);

  useEffect(() => {
    selectedAnswersRef.current = selectedAnswers;
  }, [selectedAnswers]);

  useEffect(() => {
    quizDataRef.current = quizData;
  }, [quizData]);

  useEffect(() => {
    if (quizState === 'name_required' || quizState === 'loading') {
      setTimeRemaining(timeLimit);
    }
  }, [timeLimit, quizState]);

  const checkIndicatorsOverflow = (questionCount) => {
    const el = indicatorsScrollRef.current;
    if (!el) return;
    const overflows =
      el.scrollHeight > el.clientHeight || (questionCount != null && questionCount > QUESTIONS_OVERFLOW_THRESHOLD);
    setIndicatorsOverflow(overflows);
  };

  useLayoutEffect(() => {
    if (!quizData?.questions?.length) {
      setIndicatorsOverflow(false);
      return;
    }
    const el = indicatorsScrollRef.current;
    const count = quizData.questions.length;
    if (!el) return;
    const runCheck = () => {
      requestAnimationFrame(() => checkIndicatorsOverflow(count));
    };
    runCheck();
    const t0 = setTimeout(runCheck, 0);
    const t1 = setTimeout(runCheck, 100);
    const t2 = setTimeout(runCheck, 300);
    const ro = new ResizeObserver(runCheck);
    ro.observe(el);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
    };
  }, [quizData?.questions?.length]);

  // Reset sidebar scroll to top when quiz loads so numbers 1, 2, 3... are visible
  const scrollIndicatorsToTop = () => {
    const el = indicatorsScrollRef.current;
    if (el) el.scrollTop = 0;
  };
  useLayoutEffect(() => {
    if (!quizData?.questions?.length) return;
    scrollIndicatorsToTop();
    const t0 = setTimeout(scrollIndicatorsToTop, 0);
    const t1 = setTimeout(scrollIndicatorsToTop, 150);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
    };
  }, [quizData?.questions?.length]);

  // Keep the active question number visible when navigating (skip for Q1 so we don't fight initial scroll)
  useEffect(() => {
    if (!quizData?.questions?.length || indicatorsScrollRef.current == null || currentQuestionIndex === 0) return;
    const active = indicatorsScrollRef.current.querySelector('.indicator-btn.active');
    if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentQuestionIndex, quizData?.questions?.length]);

  // Player mode flag - always true in PlayerQuiz component
  const isPlayerMode = true;

  const loadQuiz = async () => {
    if (!quizUrl) return;
    setQuizState('loading');
    setError(null);

    try {
      let arrayBuffer;

      // Firebase Storage: use getBlob (direct SDK download, no fetch/CORS issues)
      if (quizUrl.includes('firebasestorage.googleapis.com')) {
        const url = new URL(quizUrl);
        const path = url.pathname.split('/o/')[1]?.split('?')[0];
        if (!path) throw new Error('Invalid Firebase Storage URL');
        const storageRef = ref(storage, decodeURIComponent(path));
        const blob = await getBlob(storageRef);
        arrayBuffer = await blob.arrayBuffer();
      } else {
        arrayBuffer = await fetchDocxAsArrayBuffer(quizUrl);
      }
      
      // Parse DOCX using the shared parser
      const questions = await parseDocxToQuiz(arrayBuffer);
      
      if (questions.length === 0) {
        throw new Error('No valid quiz questions found in the document. Please check the format.');
      }
      
      setQuizData({
        questions: questions,
        title: 'Quiz',
        description: 'Quiz loaded from document',
        sourceUrl: quizUrl
      });
      setQuizState('playing');
      submittedRef.current = false;
      setAnswersLocked(false);
      setAnswerError('');
      if (timeLimit) {
        timerEndRef.current = Date.now() + timeLimit * 1000;
        setTimeRemaining(timeLimit);
        setIsTimerActive(true);
      }
    } catch (error) {
      console.error('Failed to load quiz:', error);
      const errorMsg = error.message || 'Failed to load quiz. Please try again.';
      setError(errorMsg);
      setQuizState('error');
      toastManager.error(errorMsg);
    }
  };

  // Timer: tick every second to update display, calculate remaining from end timestamp
  useEffect(() => {
    if (!isTimerActive || quizState !== 'playing' || !timerEndRef.current) return;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((timerEndRef.current - Date.now()) / 1000));
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        setIsTimerActive(false);
        setAnswersLocked(true);
        calculateScore();
      }
    };

    tick(); // immediate first update
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isTimerActive, quizState]);

  // Warn user when closing tab or refreshing during quiz (progress will be lost)
  useEffect(() => {
    if (quizState !== 'playing') return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [quizState]);

  const formatTime = (seconds) => {
    const s = Math.max(0, Math.floor(seconds ?? 0));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionId, answerId) => {
    if (answersLocked || submittedRef.current) return;
    setAnswerError('');
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };

  const canNavigateTo = (index) => {
    const data = quizDataRef.current;
    if (!data?.questions || answersLocked || submittedRef.current) return false;
    if (index < 0 || index >= data.questions.length) return false;
    for (let i = 0; i < index; i += 1) {
      if (!selectedAnswers[data.questions[i].id]) return false;
    }
    return true;
  };

  const handleNextQuestion = () => {
    if (!quizData || answersLocked || submittedRef.current) return;
    const question = quizData.questions[currentQuestionIndex];
    if (!selectedAnswers[question.id]) {
      setAnswerError('Please select an answer before continuing.');
      return;
    }
    setAnswerError('');
    if (currentQuestionIndex < quizData.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setConfirmModal({ type: 'finish' });
    }
  };

  const handleConfirmAction = async () => {
    if (confirmModal?.type === 'finish') {
      await calculateScore();
    }
    setConfirmModal(null);
  };

  const handlePreviousQuestion = () => {
    if (answersLocked || submittedRef.current) return;
    if (currentQuestionIndex > 0) {
      setAnswerError('');
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleIndicatorClick = (index) => {
    if (!canNavigateTo(index)) return;
    setAnswerError('');
    setCurrentQuestionIndex(index);
  };

  const buildScoreAndAnswers = () => {
    const data = quizDataRef.current;
    const answersMap = selectedAnswersRef.current;
    if (!data) {
      return { correctCount: 0, totalQuestions: 0, answers: [] };
    }

    let correctCount = 0;
    const totalQuestions = data.questions.length;

    const answers = data.questions.map((q, index) => {
      const correctAns = q.answers.find((a) => a.isCorrect);
      const selectedId = answersMap[q.id];
      const selectedAns = q.answers.find((a) => a.id === selectedId);
      const isCorrect = Boolean(selectedId && correctAns && selectedId === correctAns.id);
      if (isCorrect) {
        correctCount++;
      }
      return {
        questionId: q.id,
        questionNumber: resolveQuestionNumberFromQuizItem(q, index),
        questionText: q.text,
        selectedAnswerId: selectedId || null,
        selectedAnswerText: selectedAns?.text || '(not answered)',
        correctAnswerId: correctAns?.id || null,
        correctAnswerText: correctAns?.text || '',
        isCorrect,
      };
    });

    return { correctCount, totalQuestions, answers };
  };

  const calculateScore = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setIsTimerActive(false);
    setAnswersLocked(true);

    const data = quizDataRef.current;
    const { correctCount, totalQuestions, answers } = buildScoreAndAnswers();

    setScore(correctCount);
    setQuizState('completed');

    if (sessionId && player?.email) {
      try {
        const id = await recordCompletion({
          sessionId,
          userEmail: player.email,
          playerName: playerDisplayName?.trim() || null,
          score: correctCount,
          totalQuestions,
          sessionTitle: sessionTitle || data?.title || 'Quiz',
          answers,
        });
        if (id) setCompletionId(id);
      } catch (err) {
        console.error('Failed to record completion:', err);
      }
    }

  };

  const handleDownloadPdf = async () => {
    if (!quizData) return;
    const { correctCount, totalQuestions, answers } = buildScoreAndAnswers();

    try {
      await generateAttemptPdf({
        player,
        userEmail: player?.email,
        sessionTitle: sessionTitle || quizData?.title || 'Quiz',
        score: correctCount,
        totalQuestions,
        answers,
        completedAt: Date.now(),
      });
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    }
  };

  const currentQuestion = quizData?.questions[currentQuestionIndex];

  const handleStartQuiz = () => {
    const name = (playerDisplayName || '').trim();
    if (!name || !quizUrl) return;
    setQuizState('loading');
    loadQuiz();
  };

  const copyReportLink = () => {
    if (!completionId) return;
    const url = `${window.location.origin}/report-view?id=${encodeURIComponent(completionId)}`;
    navigator.clipboard.writeText(url).then(
      () => toastManager.success('Report link copied'),
      () => toastManager.error('Failed to copy link')
    );
  };

  if (quizUrl && quizState === 'name_required') {
    const startTitle = sessionTitle || 'Quiz';
    const startLimitSecs = Math.max(0, Math.floor(timeLimit ?? 0));
    const startLimitLabel = startLimitSecs > 0
      ? (startLimitSecs % 60 === 0
        ? `${startLimitSecs / 60} min`
        : formatTime(startLimitSecs))
      : null;
    const startRingC = 2 * Math.PI * 52;
    const startRingOffset = startRingC * 0.62;

    return (
      <div className="player-quiz-container">
        <div className="quiz-name-entry">
          <div className="name-entry-card">
            <div className="name-entry-hero" aria-hidden="true">
              <svg className="name-entry-ring" viewBox="0 0 120 120">
                <circle className="name-entry-ring-track" cx="60" cy="60" r="52" />
                <circle
                  className="name-entry-ring-fill"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{ strokeDasharray: startRingC, strokeDashoffset: startRingOffset }}
                />
              </svg>
              <div className="name-entry-ring-label">
                <ClipboardList size={32} strokeWidth={1.8} />
              </div>
            </div>

            <p className="name-entry-kicker">{startTitle}</p>
            <h2 className="name-entry-title">Enter your name</h2>
            <p className="name-entry-hint">Your name appears on your results when you finish.</p>

            <div className="name-entry-meta">
              {startLimitLabel && (
                <span className="name-entry-pill">
                  <Clock size={13} strokeWidth={2.2} aria-hidden />
                  Timed · {startLimitLabel}
                </span>
              )}
              <span className="name-entry-pill">Multiple choice</span>
            </div>

            <label htmlFor="quiz-player-name" className="name-entry-label">Display name</label>
            <input
              id="quiz-player-name"
              type="text"
              className="name-input"
              placeholder="Your name"
              value={playerDisplayName}
              onChange={(e) => setPlayerDisplayName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleStartQuiz()}
              autoFocus
              autoComplete="name"
            />
            <button
              type="button"
              className="start-quiz-btn"
              disabled={!(playerDisplayName || '').trim()}
              onClick={handleStartQuiz}
            >
              Start quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (quizState === 'loading') {
    return (
      <div className="player-quiz-container">
        <div className="quiz-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading quiz from document...</p>
        </div>
      </div>
    );
  }

  if (quizState === 'error') {
    return (
      <div className="player-quiz-container">
        <div className="quiz-error">
          <h2>Quiz Loading Failed</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button className="retry-btn" onClick={loadQuiz}>
              Try Again
            </button>
            <button className="fallback-btn" onClick={() => setQuizState('fallback')}>
              Load Sample Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (quizState === 'fallback') {
    // Render a simple fallback quiz
    return (
      <div className="player-quiz-container">
        <div className="quiz-fallback">
          <h2>Sample Quiz</h2>
          <p>This is a fallback quiz due to document parsing issues.</p>
          {/* Simple fallback quiz implementation */}
        </div>
      </div>
    );
  }

  if (quizState === 'completed') {
    const totalQuestions = quizData.questions.length;
    const percentage = Math.round((score / totalQuestions) * 100);
    const passed = percentage >= 70;
    const examinee =
      playerDisplayName?.trim() || player?.displayName || player?.email || 'there';
    const { answers: analysisAnswers } = buildScoreAndAnswers();

    let resultTitle;
    let resultSubtitle;
    if (percentage >= 90) {
      resultTitle = `Outstanding, ${examinee}!`;
      resultSubtitle = 'Excellent work — you have truly mastered this material.';
    } else if (percentage >= 70) {
      resultTitle = `Well Done, ${examinee}!`;
      resultSubtitle = 'Your understanding of the material is strong.';
    } else if (percentage >= 50) {
      resultTitle = `Good Effort, ${examinee}!`;
      resultSubtitle = "You're getting there — a little more practice will help.";
    } else {
      resultTitle = `Keep Going, ${examinee}!`;
      resultSubtitle = 'Revisit the material to strengthen your understanding.';
    }

    const resultTone = passed ? 'pass' : percentage >= 50 ? 'warn' : 'retry';
    const resultStatusLabel = passed ? 'Achieved' : percentage >= 50 ? 'Good effort' : 'Keep going';
    const ringC = 2 * Math.PI * 52;
    const ringOffset = ringC * (1 - Math.min(100, Math.max(0, percentage)) / 100);

    return (
      <div className="player-quiz-container">
        <div className="quiz-completed">
          <div className={`result-card result-card--${resultTone}`}>
            <h2 className="result-title">{resultTitle}</h2>

            <div className={`result-score-hero ${resultTone}`} aria-hidden="true">
              <svg className="result-ring" viewBox="0 0 120 120">
                <circle className="result-ring-track" cx="60" cy="60" r="52" />
                <circle
                  className="result-ring-fill"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{ strokeDasharray: ringC, strokeDashoffset: ringOffset }}
                />
              </svg>
              <div className="result-ring-label">
                <span className="result-percent">{percentage}%</span>
                {passed && <Check className="result-ring-check" size={18} strokeWidth={2.5} aria-hidden />}
              </div>
            </div>

            <div className="result-score-meta">
              <span className="result-fraction">{score} of {totalQuestions} correct</span>
              <span className={`result-status-pill ${resultTone}`}>{resultStatusLabel}</span>
            </div>

            <p className="result-subtitle">{resultSubtitle}</p>

            {canReview ? (
              <div className="review-answers-box">
                <h3>Review Your Answers</h3>
                <p>See which questions you got right and where you can improve.</p>
                <div className="result-actions">
                  <button
                    type="button"
                    className="result-btn primary"
                    onClick={() => setShowAnalysis((v) => !v)}
                  >
                    {showAnalysis ? 'Hide Detailed Analysis' : 'View Detailed Analysis'}
                  </button>
                  <button
                    type="button"
                    className="result-btn outline"
                    onClick={() => (window.location.href = '/')}
                  >
                    Back to Home
                  </button>
                  {isAdmin && (
                    <>
                      <button type="button" className="result-btn outline" onClick={handleDownloadPdf}>
                        Download PDF report
                      </button>
                      {completionId && (
                        <button type="button" className="result-btn outline" onClick={copyReportLink}>
                          <Copy size={16} aria-hidden /> Copy link
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="result-actions result-actions-simple">
                <button
                  type="button"
                  className="result-btn primary"
                  onClick={() => (window.location.href = '/')}
                >
                  Back to Home
                </button>
              </div>
            )}

            )}

            {canReview && showAnalysis && (
              <div className="result-analysis">
                <table className="result-analysis-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Your Answer</th>
                      <th>Correct Answer</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisAnswers.map((a, i) => (
                      <tr key={i} className={a.isCorrect ? 'correct' : 'incorrect'}>
                        <td>{i + 1}</td>
                        <td>{a.questionText}</td>
                        <td>{a.selectedAnswerText || '(not answered)'}</td>
                        <td>{a.correctAnswerText || '—'}</td>
                        <td>{a.isCorrect ? 'Correct' : 'Wrong'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const useStartAlignment =
    indicatorsOverflow || (quizData?.questions?.length ?? 0) > QUESTIONS_OVERFLOW_THRESHOLD;

  return (
    <div className="player-quiz-container">
      <div className="player-quiz-layout">
        <aside className="quiz-sidebar">
          <div
            ref={indicatorsScrollRef}
            className={`question-indicators ${!useStartAlignment ? 'question-indicators--fits' : ''}`}
          >
            {quizData.questions.map((q, index) => (
              <button
                key={q.id}
                type="button"
                className={`indicator-btn ${
                  index === currentQuestionIndex ? 'active' : ''
                } ${
                  selectedAnswers[q.id] ? 'answered' : ''
                } ${
                  !canNavigateTo(index) ? 'locked' : ''
                }`}
                onClick={() => handleIndicatorClick(index)}
                disabled={!canNavigateTo(index)}
                aria-disabled={!canNavigateTo(index)}
              >
                {q.id}
              </button>
            ))}
          </div>
        </aside>

        <main className="quiz-main">
          <div className="quiz-header">
            <div className="quiz-header-main">
              <h1>{quizData?.title || 'Quiz'}</h1>
              <div className="quiz-header-actions">
                {timeLimit && (
                  <div className={`timer-display timer-top ${timeRemaining <= 60 ? 'timer-warning' : ''} ${timeRemaining <= 30 ? 'timer-critical' : ''}`}>
                    <Clock className="timer-icon" size={18} />
                    <span className="timer-text">{formatTime(timeRemaining)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="progress-info">
              <div className="progress-row">
                <span className="progress-text">
                  Question {currentQuestionIndex + 1} of {quizData?.questions.length}
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${((currentQuestionIndex + 1) / quizData.questions.length) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="quiz-content">
            <div className="question-card">
              <div className="question-number">Question {currentQuestion.id}</div>
              <div className="question-text">{currentQuestion.text}</div>
              
              <div className="answers-container">
                {(currentQuestion.answers || []).map((answer) => (
                  <button
                    type="button"
                    key={answer.id}
                    className={`answer-btn ${
                      selectedAnswers[currentQuestion.id] === answer.id ? 'selected' : ''
                    }`}
                    onClick={() => handleAnswerSelect(currentQuestion.id, answer.id)}
                    disabled={answersLocked}
                  >
                    <span className="answer-letter">{answer.id}</span>
                    <span className="answer-text">{answer.text}</span>
                  </button>
                ))}
              </div>

              <div className="quiz-nav-under-choices">
                <button 
                  className="control-btn prev-btn"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0 || answersLocked}
                >
                  Previous
                </button>
                <button 
                  className="control-btn next-btn"
                  onClick={handleNextQuestion}
                  disabled={answersLocked}
                >
                  {currentQuestionIndex === quizData.questions.length - 1 ? 'Finish' : 'Next'}
                </button>
              </div>
              {answerError && (
                <p className="answer-required-msg" role="alert">{answerError}</p>
              )}
            </div>
          </div>
        </main>
      </div>

      {confirmModal && (
        <div className="confirm-overlay" onClick={() => setConfirmModal(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Are you sure?</h3>
            <p>
              Are you sure you want to finish the quiz? You cannot go back after submitting.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn cancel-btn"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-primary"
                onClick={handleConfirmAction}
              >
                Finish Quiz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerQuiz;
